import { useEffect } from 'react';
import { useBlocker } from 'react-router-dom';

interface NavigationBlockerProps {
  /** Arm the blocker while true; never block while false. */
  when: boolean;
  /**
   * Called when a navigation is blocked. `proceed` resumes the attempted
   * navigation; `reset` cancels it and stays on the current page.
   */
  onBlock: (proceed: () => void, reset: () => void) => void;
}

/**
 * react-router v5 `<Prompt>` replacement for the data router.
 *
 * v7 removed `<Prompt>`; in-app navigation blocking now goes through
 * `useBlocker`, which only exists on a data router (`createBrowserRouter` +
 * `<RouterProvider>` — wired up at the root in src/index.js).
 *
 * Contract — preserves the exact v5 `<Prompt when message>` UX:
 *
 *   - `when` true  -> arm the blocker. An attempted navigation pauses with
 *     `blocker.state === 'blocked'`.
 *   - on block, call `onBlock(proceed, reset)`:
 *       - `proceed()` -> let the original navigation continue (`blocker.proceed`)
 *       - `reset()`   -> cancel and stay (`blocker.reset`)
 *   - `when` false -> never blocks (matches the old `when={this.inspectorDirty}`).
 *
 * This drops the v5 `confirmedNavigation` re-navigation bookkeeping entirely:
 * `proceed()` resumes the very navigation the user attempted, so there is no
 * need to re-issue `history.push(nextLocation)` after confirming.
 */
export default function NavigationBlocker({ when, onBlock }: NavigationBlockerProps): null {
  const blocker = useBlocker(when);

  useEffect(() => {
    if (blocker.state === 'blocked') {
      onBlock(blocker.proceed, blocker.reset);
    }
  }, [blocker, onBlock]);

  return null;
}
